import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, MapPin, Ticket, ArrowLeft, TrafficCone } from "lucide-react";
import { api } from "@/lib/api";

const WINDOWS = [7, 14, 30];

function HotspotList({ title, icon: Icon, rows, unit, empty, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.occurrences));
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        <span className="ml-auto text-xs text-slate-400">{rows.length} {unit}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">{empty}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r, i) => (
            <li key={r.name} data-testid={`hotspot-row-${i}`}>
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-rose-400 shrink-0" /> {r.name}
                </span>
                <span className="text-xs font-bold text-rose-600 tabular-nums shrink-0">{r.occurrences}×</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-rose-400" style={{ width: `${Math.round((r.occurrences / max) * 100)}%` }} />
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-400">
                <span>{r.runs} run{r.runs !== 1 ? "s" : ""} affected</span>
                {r.delay_mins > 0 && <span>· {r.delay_mins} min total delay logged</span>}
                {r.run_names?.length > 0 && <span className="truncate">· {r.run_names.join(", ")}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function HotspotReport() {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setData(null); setErr(false);
    api.get(`/disruption/hotspots?days=${days}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [days]);

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8 w-full" data-testid="page-hotspot-report">
      <button onClick={() => navigate("/runs")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-4" data-testid="hotspot-back">
        <ArrowLeft className="w-4 h-4" /> All runs
      </button>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <PageIcon />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Disruption Hotspots</h1>
          <p className="text-sm text-slate-500 mt-0.5">The roads and event venues most often disrupting runs</p>
        </div>
        <div className="ml-auto inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="hotspot-window">
          {WINDOWS.map((w) => (
            <button key={w} onClick={() => setDays(w)} data-testid={`hotspot-window-${w}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${days === w ? "bg-indigo-600 text-white" : "text-slate-600 hover:text-indigo-600"}`}>
              {w} days
            </button>
          ))}
        </div>
      </div>

      {err ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="hotspot-error">
          Could not load the hotspot report.
        </div>
      ) : data === null ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Building report…</div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-4" data-testid="hotspot-meta">
            Based on {data.history_entries} disruption scan{data.history_entries !== 1 ? "s" : ""} across {data.runs_scanned} run{data.runs_scanned !== 1 ? "s" : ""} over the last {data.window_days} days.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <HotspotList title="Disruptive roads" icon={TrafficCone} rows={data.roads} unit="roads"
              empty="No recurring road disruptions logged in this window." testid="hotspot-roads" />
            <HotspotList title="Event venues" icon={Ticket} rows={data.venues} unit="venues"
              empty="No recurring event-venue disruptions logged in this window." testid="hotspot-venues" />
          </div>
        </>
      )}
    </div>
  );
}
